import React from 'react';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const TermsOfService: React.FC = () => {
  // SEO optimization for Terms of Service page
  useDocumentTitle({
    title: 'Terms of Service - WageWise | H1B Visa Job Search Platform',
    description: 'Read WageWise terms of service to understand the rules and guidelines for using our H1B visa job search and sponsorship platform.',
    keywords: 'terms of service, user agreement, WageWise, H1B visa, job search, terms and conditions',
    canonicalUrl: 'https://wagewise.app/terms',
    noIndex: false
  });

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <header className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Terms of Service</h1>
        <p className="text-gray-600">Terms and conditions for using WageWise H1B job search platform</p>
      </header>
      
      <div className="prose prose-gray max-w-none">
        <p className="mb-6 text-sm text-gray-500">Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
        
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">Introduction</h2>
          <p className="mb-4">
            Welcome to WageWise. These Terms of Service ("Terms") govern your access to and use of our website and services for H1B visa job search and sponsorship information. By accessing or using our services, you agree to be bound by these Terms.
          </p>
        </section>
        
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">Using WageWise</h2>
          <p className="mb-4">
            You may use our H1B job search services only if you can form a binding contract with WageWise, and only in compliance with these Terms and all applicable laws. When you create an account, you must provide accurate and complete information.
          </p>
          <p className="mb-4">
            You are responsible for safeguarding your password. We encourage you to use a strong password and to sign out from your account after using a shared computer.
          </p>
        </section>
        
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">Content and Conduct</h2>
          <p className="mb-4">
            You retain ownership of any content you submit through our services. By submitting content, you grant us a worldwide, non-exclusive, royalty-free license to use, copy, modify, and display that content in connection with our H1B job search services.
          </p>
          <p className="mb-3">
            You agree not to engage in any of the following prohibited activities:
          </p>
          <ul className="list-disc pl-5 space-y-1 mb-4">
            <li>Violate any laws or regulations</li>
            <li>Infringe on the rights of others</li>
            <li>Submit false or misleading information</li>
            <li>Interfere with or disrupt our services</li>
            <li>Attempt to gain unauthorized access to our systems</li>
            <li>Use our services for any illegal or unauthorized purpose</li>
            <li>Scrape or harvest data from our platform without permission</li>
          </ul>
        </section>
        
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">H1B Data and Information</h2>
          <p className="mb-4">
            The H1B sponsorship data and job information provided on our platform is based on publicly available government records and historical data. While we strive for accuracy, we cannot guarantee that all information is current or complete.
          </p>
          <p className="mb-4">
            <strong>Important:</strong> H1B sponsorship availability may change, and companies may modify their sponsorship policies at any time. Always verify current sponsorship status directly with potential employers.
          </p>
        </section>
        
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">Intellectual Property</h2>
          <p className="mb-4">
            The WageWise platform, including its design, functionality, and content, is protected by copyright, trademark, and other intellectual property laws. You may not reproduce, distribute, or create derivative works without our written permission.
          </p>
        </section>
        
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">Termination</h2>
          <p className="mb-4">
            We may suspend or terminate your access to our services at any time, without prior notice or liability, for any reason, including if you breach these Terms. Upon termination, your right to use our services will cease immediately.
          </p>
        </section>
        
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">Disclaimer of Warranties</h2>
          <p className="mb-4">
            Our services are provided "as is" without warranties of any kind, either express or implied. We do not guarantee the accuracy, completeness, or usefulness of any H1B sponsorship information on our services, and we cannot ensure that any information you access through our services is accurate or reliable.
          </p>
        </section>
        
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">Limitation of Liability</h2>
          <p className="mb-4">
            In no event shall WageWise be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your use of our services.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">Governing Law</h2>
          <p className="mb-4">
            These Terms shall be interpreted and governed by the laws of the United States, without regard to its conflict of law provisions. Any disputes arising from these Terms or your use of our services will be resolved through binding arbitration.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">Changes to Terms</h2>
          <p className="mb-4">
            We reserve the right to modify these Terms at any time. We will notify users of any changes by posting the new Terms on this page and updating the "Last updated" date. Your continued use of our services after such changes constitutes acceptance of the new Terms.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">Contact Information</h2>
          <p>
            If you have any questions about these Terms of Service, please contact us through our <a href="/contact" className="text-blue-600 hover:text-blue-800 underline">contact page</a>.
          </p>
        </section>
      </div>
    </div>
  );
};

export default TermsOfService; 